import type { ReactElement, ReactNode, SVGProps } from 'react';
import { cn } from '@/components/ui';
import type { HighlightIconId } from './highlightIconIds';

export type HighlightGlyphProps = SVGProps<SVGSVGElement>;

const defaults = {
  width: 24,
  height: 24,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.75,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

function Glyph({
  className,
  children,
  ...rest
}: HighlightGlyphProps & { children: ReactNode }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" aria-hidden className={cn(className)} {...defaults} {...rest}>
      {children}
    </svg>
  );
}

export const HIGHLIGHT_GLYPHS: Record<HighlightIconId, (props: HighlightGlyphProps) => ReactElement> = {
  shield: (p) => (
    <Glyph {...p}>
      <path d="M12 3l7 3.5v5c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9v-5L12 3z" />
      <path d="M9 12l2 2 4-4" />
    </Glyph>
  ),
  zap: (p) => (
    <Glyph {...p}>
      <path d="M13 2L4 14h7l-1 8 9-12h-7l1-8z" />
    </Glyph>
  ),
  globe: (p) => (
    <Glyph {...p}>
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18M12 3a14 14 0 010 18M12 3a14 14 0 000 18" />
    </Glyph>
  ),
  sparkles: (p) => (
    <Glyph {...p}>
      <path d="M12 3v4M12 17v4M5 5l3 3M16 16l3 3M3 12h4M17 12h4M5 19l3-3M16 8l3-3" />
      <circle cx="12" cy="12" r="2.5" fill="currentColor" stroke="none" />
    </Glyph>
  ),
  cloud: (p) => (
    <Glyph {...p}>
      <path d="M7 18h10a4 4 0 000-8 5 5 0 00-9.5-1.5A3.5 3.5 0 007 18z" />
    </Glyph>
  ),
  robot: (p) => (
    <Glyph {...p}>
      <rect x="6" y="8" width="12" height="10" rx="2" />
      <path d="M9 12h.01M15 12h.01M9 16h6M12 8V5M8 5h8" />
    </Glyph>
  ),
  link: (p) => (
    <Glyph {...p}>
      <path d="M10 13a3 3 0 004 0l2-2a3 3 0 00-4-4l-1 1M14 11a3 3 0 00-4 0l-2 2a3 3 0 004 4l1-1" />
    </Glyph>
  ),
  lock: (p) => (
    <Glyph {...p}>
      <rect x="5" y="11" width="14" height="10" rx="2" />
      <path d="M8 11V8a4 4 0 118 0v3" />
    </Glyph>
  ),
  users: (p) => (
    <Glyph {...p}>
      <circle cx="9" cy="8" r="3" />
      <path d="M3 19c0-3 2.5-5 6-5s6 2 6 5M16 11a2.5 2.5 0 100-5M19 19c0-2-1.5-3.5-3-3.5" />
    </Glyph>
  ),
  bank: (p) => (
    <Glyph {...p}>
      <path d="M3 10h18M5 10v8M9 10v8M15 10v8M19 10v8M2 18h20M12 4l9 6H3l9-6z" />
    </Glyph>
  ),
  health: (p) => (
    <Glyph {...p}>
      <path d="M12 21s-7-4.5-7-11a4 4 0 017-2 4 4 0 017 2c0 6.5-7 11-7 11z" />
      <path d="M12 8v6M9 11h6" />
    </Glyph>
  ),
  cart: (p) => (
    <Glyph {...p}>
      <circle cx="9" cy="20" r="1" />
      <circle cx="18" cy="20" r="1" />
      <path d="M2 4h2l2 12h12l2-8H6" />
    </Glyph>
  ),
  factory: (p) => (
    <Glyph {...p}>
      <path d="M3 21V9l6 4V9l6 4V9l6 4v8H3z" />
      <path d="M9 17v4M15 17v4" />
    </Glyph>
  ),
  signal: (p) => (
    <Glyph {...p}>
      <path d="M2 20h.01M7 16h.01M12 12h.01M17 8h.01M22 4h.01" />
    </Glyph>
  ),
  government: (p) => (
    <Glyph {...p}>
      <path d="M4 20V10l8-6 8 6v10M4 20h16M9 20v-5h6v5" />
    </Glyph>
  ),
  film: (p) => (
    <Glyph {...p}>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="M7 5v14M12 10v4M17 8v8" />
    </Glyph>
  ),
  compass: (p) => (
    <Glyph {...p}>
      <circle cx="12" cy="12" r="9" />
      <path d="M16 8l-2 6-6 2 2-6 6-2z" />
    </Glyph>
  ),
  tools: (p) => (
    <Glyph {...p}>
      <path d="M14 6l4 4-6 6H6v-4l6-6zM8 16l2 2" />
    </Glyph>
  ),
  'chart-trend': (p) => (
    <Glyph {...p}>
      <path d="M3 20h18M7 16l4-6 4 3 5-8" />
    </Glyph>
  ),
  ruler: (p) => (
    <Glyph {...p}>
      <path d="M4 16l12-12 4 4-12 12-4-4zM14 6l2 2" />
    </Glyph>
  ),
  wrench: (p) => (
    <Glyph {...p}>
      <path d="M14 4a4 4 0 00-5 5L4 14a2 2 0 103 3l5-5a4 4 0 005-5l-3 3" />
    </Glyph>
  ),
  check: (p) => (
    <Glyph {...p}>
      <circle cx="12" cy="12" r="9" />
      <path d="M8 12l3 3 5-6" />
    </Glyph>
  ),
  settings: (p) => (
    <Glyph {...p}>
      <circle cx="12" cy="12" r="3" />
      <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
    </Glyph>
  ),
  refresh: (p) => (
    <Glyph {...p}>
      <path d="M4 12a8 8 0 0113.5-5.5M20 12a8 8 0 01-13.5 5.5M4 4v4h4M20 20v-4h-4" />
    </Glyph>
  ),
  book: (p) => (
    <Glyph {...p}>
      <path d="M5 4h7a3 3 0 013 3v13a3 3 0 00-3-3H5zM19 4h-7a3 3 0 00-3 3v13a3 3 0 013-3h7z" />
    </Glyph>
  ),
  flask: (p) => (
    <Glyph {...p}>
      <path d="M9 3h6M10 9h4l3 9a3 3 0 01-6 0l3-9z" />
    </Glyph>
  ),
  user: (p) => (
    <Glyph {...p}>
      <circle cx="12" cy="8" r="3" />
      <path d="M5 20c0-4 3-6 7-6s7 2 7 6" />
    </Glyph>
  ),
  'chart-bar': (p) => (
    <Glyph {...p}>
      <path d="M4 20V10M10 20V6M16 20v-8M22 20V4" />
    </Glyph>
  ),
  calendar: (p) => (
    <Glyph {...p}>
      <rect x="4" y="5" width="16" height="16" rx="2" />
      <path d="M8 3v4M16 3v4M4 11h16" />
    </Glyph>
  ),
  folder: (p) => (
    <Glyph {...p}>
      <path d="M4 7h5l2 2h9v10H4V7z" />
    </Glyph>
  ),
  puzzle: (p) => (
    <Glyph {...p}>
      <path d="M8 4a2 2 0 104 0h4a2 2 0 104 0v4a2 2 0 10-4 0H8V4zM8 14a2 2 0 104 0v4h4a2 2 0 10-4 0v-4H8z" />
    </Glyph>
  ),
  target: (p) => (
    <Glyph {...p}>
      <circle cx="12" cy="12" r="8" />
      <circle cx="12" cy="12" r="4" />
      <circle cx="12" cy="12" r="1" fill="currentColor" stroke="none" />
    </Glyph>
  ),
  handshake: (p) => (
    <Glyph {...p}>
      <path d="M4 12l3 3 4-4 3 3 6-6M8 15l2 2M12 11l2 2" />
    </Glyph>
  ),
  briefcase: (p) => (
    <Glyph {...p}>
      <rect x="3" y="8" width="18" height="12" rx="2" />
      <path d="M8 8V6a2 2 0 012-2h4a2 2 0 012 2v2M12 12v2" />
    </Glyph>
  ),
  building: (p) => (
    <Glyph {...p}>
      <path d="M4 20V8l4-2v14M12 20V4l4-2v18M20 20V10l-4-2v12" />
    </Glyph>
  ),
  phone: (p) => (
    <Glyph {...p}>
      <path d="M6 4h4l2 5-2 1a11 11 0 005 5l1-2 5 2v4a2 2 0 01-2 2A14 14 0 016 6a2 2 0 012-2z" />
    </Glyph>
  ),
  key: (p) => (
    <Glyph {...p}>
      <circle cx="8" cy="15" r="4" />
      <path d="M12 15h9M18 12v6" />
    </Glyph>
  ),
  clipboard: (p) => (
    <Glyph {...p}>
      <rect x="6" y="5" width="12" height="16" rx="2" />
      <path d="M9 5h6a2 2 0 010 4H9a2 2 0 010-4zM9 12h6M9 16h4" />
    </Glyph>
  ),
  plug: (p) => (
    <Glyph {...p}>
      <path d="M9 7v4M15 7v4M7 11h10v3a5 5 0 01-10 0v-3zM12 17v4" />
    </Glyph>
  ),
  pen: (p) => (
    <Glyph {...p}>
      <path d="M4 20l4-1 9-9-3-3-9 9-1 4zM14 6l3 3" />
    </Glyph>
  ),
  archive: (p) => (
    <Glyph {...p}>
      <rect x="4" y="4" width="16" height="6" rx="1" />
      <path d="M6 10h12v10H6zM10 14h4" />
    </Glyph>
  ),
  currency: (p) => (
    <Glyph {...p}>
      <circle cx="12" cy="12" r="8" />
      <path d="M12 7v10M9 10c0-1 1.5-2 3-2s3 1 3 2-1.5 2-3 2-3 1-3 2 1.5 2 3 2" />
    </Glyph>
  ),
  graduation: (p) => (
    <Glyph {...p}>
      <path d="M3 10l9-5 9 5-9 5-9-5zM6 12v5c0 2 4 3 6 3s6-1 6-3v-5" />
    </Glyph>
  ),
  video: (p) => (
    <Glyph {...p}>
      <rect x="3" y="6" width="13" height="12" rx="2" />
      <path d="M16 10l5-3v10l-5-3v-4z" />
    </Glyph>
  ),
  trophy: (p) => (
    <Glyph {...p}>
      <path d="M8 4h8v3a4 4 0 01-8 0V4zM6 4H4a2 2 0 002 2M20 4h-2a2 2 0 012 2M12 14v3M8 20h8M10 17h4" />
    </Glyph>
  ),
  layers: (p) => (
    <Glyph {...p}>
      <path d="M12 3l9 5-9 5-9-5 9-5zM3 12l9 5 9-5M3 17l9 5 9-5" />
    </Glyph>
  ),
};
